/* eslint-disable no-mixed-operators */
import isEqual from 'lodash-es/isEqual'
import get from 'lodash-es/get'
import sortBy from 'lodash-es/sortBy'
import upperFirst from 'lodash-es/upperFirst'
import flatMap from 'lodash-es/flatMap'
import Units from './units'
import { isTouchDevice, guid } from 'lib/utils'
import OBJECT_TYPES from 'config/objectTypes'

import * as THREE from 'three'
import { vectorModelToUI, vectorUIToModel } from '~/components/DrawingCanvas/util/units'
import store from '~/store'

const directions = [
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, -1, 0),
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(-1, 0, 0),
  new THREE.Vector3(1, 1, 0),
  new THREE.Vector3(1, -1, 0),
  new THREE.Vector3(-1, 1, 0),
  new THREE.Vector3(-1, -1, 0),
]

class Util {
  // Works for points in object or array format
  static pointsAreEqual2D(p1, p2, threshold = 0.001) {
    if (!p1 || !p2) return false

    // object style
    if (p1.x !== undefined) {
      return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2) < threshold
    }

    // array style
    return Math.sqrt((p2[0] - p1[0]) ** 2 + (p2[1] - p1[1]) ** 2) < threshold
  }

  static pointsAreEqual3D(p1 = {}, p2 = {}, threshold = 0.001) {
    return (
      Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2 + (p2.z - p1.z) ** 2) <
      threshold
    )
  }

  static slopeFromPoints(firstPoint, secondPoint) {
    let point1 = firstPoint
    let point2 = secondPoint

    // Convert to object style point if necessary
    if (point1.x === undefined) {
      point1 = Util.arrayPointToObjectPoint(point1)
      point2 = Util.arrayPointToObjectPoint(point2)
    }

    let xChange = point2.x - point1.x
    let yChange = point2.y - point1.y

    // Horizontal
    if (Math.abs(xChange) < 0.00001) {
      xChange = 0.00001
    }

    // Vertical
    if (Math.abs(yChange) < 0.00001) {
      yChange = 0.00001
    }

    const slope = yChange / xChange

    return slope
  }

  static isPolygonValid(polygon) {
    if (!polygon || polygon.length < 3) {
      return false
    }

    if (polygon[0].x === undefined && polygon[0][0] !== undefined) {
      polygon = polygon.map(point => Util.arrayPointToObjectPoint(point))
    }

    const firstAndLastPointsMatch = Util.pointsAreEqual2D(
      polygon[0],
      polygon[polygon.length - 1]
    )
    const greaterThanZeroArea =
      Util.polygonArea(polygon) > Units.inchesToNative(0.1)

    return firstAndLastPointsMatch && greaterThanZeroArea
  }

  // Gets the position between two vectors at a given distance
  static getPositionBetweenTwoVectors(vectorA, vectorB, length) {
    const startVector = new THREE.Vector3().copy(vectorA)
    const endVector = new THREE.Vector3().copy(vectorB)

    const direction = endVector
      .clone()
      .sub(startVector)
      .normalize()
      .multiplyScalar(length)
    return startVector.clone().add(direction)
  }

  // Gets the direction from the origin vector to the target vector
  static getVectorToVectorDirection(target, origin) {
    const direction = new THREE.Vector3().subVectors(target, origin)
    direction.set(direction.x, direction.y, 0).normalize()

    return direction
  }

  static arrayPointToObjectPoint(arrayPoint) {
    return new THREE.Vector3(arrayPoint[0], arrayPoint[1], 0)
  }

  // From: https://github.com/substack/point-in-polygon
  static isPointInPolygon(point, polygonPoints) {
    // ray-casting algorithm based on
    // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html

    polygonPoints = Util.toVec3Array(polygonPoints)

    const x = point.x
    const y = point.y

    let inside = false
    let i = 0
    for (
      let j = polygonPoints.length - 1;
      i < polygonPoints.length;
      j = i - 1
    ) {
      const xi = polygonPoints[i].x
      const yi = polygonPoints[i].y
      const xj = polygonPoints[j].x
      const yj = polygonPoints[j].y

      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
      if (intersect) inside = !inside

      i += 1
    }

    return inside
  }

  /*
    From: https://github.com/math-utils/area-polygon
  */
  static polygonArea(points = []) {
    let det = 0

    points = points.map(point => {
      if (!Array.isArray(point)) return point
      return {
        x: point[0],
        y: point[1],
      }
    })

    if (!Util.pointsAreEqual2D(points[0], points[points.length - 1]))
      points = points.concat(points[0])

    for (let i = 0; i < points.length - 1; i += 1)
      det += points[i].x * points[i + 1].y - points[i].y * points[i + 1].x

    return Math.abs(det) / 2
  }

  /**
   * @param {THREE.Vector3Like[]|THREE.Vector3Tuple[]} points
   * @returns {THREE.Vector3}
   */
  static polygonCentroid(points) {
    points = points.map(point => {
      // Convert to object style if necessary
      const finalPoint = !Array.isArray(point)
        ? {
          x: point.x,
          y: point.y,
          z: point.z,
        }
        : {
          x: point[0],
          y: point[1],
          z: 0,
        }

      // Ensure there is a z-coord
      finalPoint.z = finalPoint.z || 0

      return finalPoint
    })

    // Ensure last point is the first point repeated
    if (Util.pointsAreEqual3D(points[0], points[points.length - 1]))
      points.pop()

    const l = points.length

    return points.reduce((center, p, i) => {
      center.x += p.x
      center.y += p.y
      center.z += p.z

      if (i === l - 1) {
        center.x /= l
        center.y /= l
        center.z /= l
      }

      return center
    }, new THREE.Vector3(0, 0, 0))
  }

  // Just used by `lineSegmentsIntersectionPoint(...)`
  static _between(a, b, c) {
    const eps = 0.0000001
    return a - eps <= b && b <= c + eps
  }

  /*
    If the line segments intersect, the intersection point is
    returned in the format `{x, y}`; otherwise `false` is returned.
  */
  static lineSegmentsIntersectionPoint(
    line1Start,
    line1End,
    line2Start,
    line2End
  ) {
    const x1 = line1Start.x
    const y1 = line1Start.y
    const x2 = line1End.x
    const y2 = line1End.y
    const x3 = line2Start.x
    const y3 = line2Start.y
    const x4 = line2End.x
    const y4 = line2End.y

    var x =
      ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) /
      ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4))
    var y =
      ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) /
      ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4))
    if (isNaN(x) || isNaN(y)) {
      return false
    } else {
      if (x1 >= x2) {
        if (!Util._between(x2, x, x1)) {
          return false
        }
      } else {
        if (!Util._between(x1, x, x2)) {
          return false
        }
      }
      if (y1 >= y2) {
        if (!Util._between(y2, y, y1)) {
          return false
        }
      } else {
        if (!Util._between(y1, y, y2)) {
          return false
        }
      }
      if (x3 >= x4) {
        if (!Util._between(x4, x, x3)) {
          return false
        }
      } else {
        if (!Util._between(x3, x, x4)) {
          return false
        }
      }
      if (y3 >= y4) {
        if (!Util._between(y4, y, y3)) {
          return false
        }
      } else {
        if (!Util._between(y3, y, y4)) {
          return false
        }
      }
    }
    return { x: x, y: y }
  }

  /**
   * @param {[number, number] | { x: number; y: number }} pointA
   * @param {[number, number] | { x: number; y: number }} pointB
   */
  static lineFromPoints(pointA, pointB) {
    let point1 = pointA
    let point2 = pointB

    // Convert to object style point if necessary
    if (point1.x === undefined) {
      point1 = Util.arrayPointToObjectPoint(point1)
      point2 = Util.arrayPointToObjectPoint(point2)
    }

    const slope = Util.slopeFromPoints(point1, point2)
    const yShift = point1.y - slope * point1.x

    return { slope, yShift }
  }

  static getIntersectionPoint(slope1, yShift1, slope2, yShift2) {
    const intersectionX = (yShift2 - yShift1) / (slope1 - slope2)
    const intersectionY = slope2 * intersectionX + yShift2

    return new THREE.Vector3(intersectionX, intersectionY, 0)
  }

  /**
   * Calculate the square of the distance between a finite line segment and a point. This
   * version takes somewhat less convenient parameters than distanceToLineSegment.squared,
   * but is more efficient if you are calling it multiple times for the same line segment,
   * since you pass in some easily pre-calculated values for the segment.
   * @param {number} lx1 - x-coordinate of line segment's first point
   * @param {number} ly1 - y-coordinate of line segment's first point
   * @param {number} ldx - x-coordinate of the line segment's second point minus lx1
   * @param {number} ldy - y-coordinate of the line segment's second point minus ly1
   * @param {number} lineLengthSquared - must be ldx\*ldx + ldy\*ldy. Remember, this precalculation
   *    is for efficiency when calling this multiple times for the same line segment.
   * @param {number} px - x coordinate of point
   * @param {number} py - y coordinate of point
   */
  static _distanceSquaredToLineSegment2(
    lx1,
    ly1,
    ldx,
    ldy,
    lineLengthSquared,
    px,
    py
  ) {
    let t // t===0 at line pt 1 and t ===1 at line pt 2
    if (!lineLengthSquared) {
      // 0-length line segment. Any t will return same result
      t = 0
    } else {
      t = ((px - lx1) * ldx + (py - ly1) * ldy) / lineLengthSquared

      if (t < 0) t = 0
      else if (t > 1) t = 1
    }

    const lx = lx1 + t * ldx
    const ly = ly1 + t * ldy
    const dx = px - lx
    const dy = py - ly
    return dx * dx + dy * dy
  }

  /**
   * Calculate the square of the distance between a finite line segment and a point.
   * @param {number} lx1 - x-coordinate of line segment's first point
   * @param {number} ly1 - y-coordinate of line segment's first point
   * @param {number} lx2 - x-coordinate of the line segment's second point
   * @param {number} ly2 - y-coordinate of the line segment's second point
   * @param {number} px - x coordinate of point
   * @param {number} py - y coordinate of point
   */
  static _distanceSquaredToLineSegment(lx1, ly1, lx2, ly2, px, py) {
    const ldx = lx2 - lx1
    const ldy = ly2 - ly1
    const lineLengthSquared = ldx * ldx + ldy * ldy
    return Util._distanceSquaredToLineSegment2(
      lx1,
      ly1,
      ldx,
      ldy,
      lineLengthSquared,
      px,
      py
    )
  }

  /**
  * From: https://github.com/scottglz/distance-to-line-segment

  * Calculate the distance between a finite line segment and a point. Using distanceToLineSegment.squared can often be more efficient.
  * @param {number} lx1 - x-coordinate of line segment's first point
  * @param {number} ly1 - y-coordinate of line segment's first point
  * @param {number} lx2 - x-coordinate of the line segment's second point
  * @param {number} ly2 - y-coordinate of the line segment's second point
  * @param {number} px - x coordinate of point
  * @param {number} py - y coordinate of point
  */
  static distanceToLineSegment(lx1, ly1, lx2, ly2, px, py) {
    return Math.sqrt(
      Util._distanceSquaredToLineSegment(lx1, ly1, lx2, ly2, px, py)
    )
  }

  static isFirstPointRepeated(
    polygon,
    distanceThreshold = Units.inchesToNative(0.01)
  ) {
    return Util.pointsAreEqual2D(
      polygon[0],
      polygon[polygon.length - 1],
      distanceThreshold
    )
  }

  static getCounterClockwisePoints(points) {
    if (points.length > 2 && !Util.pointsAreCounterClockwise(points)) {
      return points.slice().reverse()
    }

    return points.slice()
  }

  // See: https://stackoverflow.com/questions/1165647/how-to-determine-if-a-list-of-polygon-points-are-in-clockwise-order
  static pointsAreCounterClockwise(points) {
    if (points.length < 3) {
      console.error(
        "'polygon' of fewer than three points passed to pointsAreCounterClockwise: ",
        points
      )
      return false
    }

    points = Util.toVec3Array(points)
    if (!points || !points.length) return false

    if (!Util.isFirstPointRepeated(points)) {
      if (points[0] instanceof THREE.Vector3) points.push(points[0].clone())
    }

    const edgeCount = points.length - 1

    let sum = 0

    for (let i = 0; i < edgeCount; i += 1) {
      sum += (points[i + 1].x - points[i].x) * (points[i + 1].y + points[i].y)
    }

    return sum < 0
  }

  // See: https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
  static guid() {
    return guid()
  }

  /**
   * @template {THREE.Vector3 | THREE.Vector2} T
   *
   * @arg {T} point
   * @arg {T[]} points
   */
  static getClosestPoint(point, points) {
    let closestPoint = points[0]
    let closestDistance = point.distanceTo(closestPoint)
    for (let i = 1; i < points.length; ++i) {
      const p = points[i]
      const distance = point.distanceTo(p)
      if (distance < closestDistance) {
        closestPoint = p
        closestDistance = distance
      }
    }

    return closestPoint
  }

  static getFurthestTwoPoints(points) {
    let greatestDistance = 0
    let furthestPoints = []
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i]
      if (!(p1 instanceof THREE.Vector2) && !(p1 instanceof THREE.Vector3))
        continue
      for (let j = i + 1; j < points.length; j++) {
        const p2 = points[j]
        const distance = p1
          .clone()
          .sub(p2)
          .length()
        if (distance >= greatestDistance) {
          furthestPoints = [p1, p2]
          greatestDistance = distance
        }
      }
    }

    return furthestPoints
  }

  /*
    Takes array style points (e.g. [5, 5, 5]) or object style
    points: {x, y, z}, and maps them to THREE.Vector3 objects.

    If given an empty array or an array of THREE.Vector3 objects,
    a copy of the input will be returned.
  */
  /**
   * @arg {(THREE.Vector3Like | THREE.Vector3Tuple)[]} points
   * @returns {THREE.Vector3[]}
   */
  static toVec3Array(points) {
    if (!points) {
      return points
    } else if (points.length === 0) {
      return [] // This way we always return a copy.
    }

    if (!(points[0] instanceof THREE.Vector3)) {
      // Object style
      if (!isNaN(points[0].x)) {
        const z = points[0].z !== undefined ? points[0].z : 0
        return points.map(point => new THREE.Vector3(point.x, point.y, z))
      } else if (!isNaN(points[0][0])) {
        // Array style
        const z = points[0][2] !== undefined ? points[0][2] : 0
        return points.map(point => new THREE.Vector3(point[0], point[1], z))
      } else {
        // Unknown format
        return points.slice()
      }
    } else {
      return points.slice()
    }
  }
}

export default Util
